import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LandscapeIcon from '@mui/icons-material/Landscape';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import { formatDistance, formatDuration, formatElevation, getBikeProfileDisplayName } from '../../utils/routeFormatters';

export interface RouteSummaryProps {
  distance?: number | string;
  duration?: number | string;
  elevationGain?: number | string;
  bikeProfile?: string; // raw key
  elevationLabel?: string; // allow custom label (Elevation vs Gain)
  compact?: boolean; // smaller padding variant
}

/**
 * RouteSummary - shared read-only route metrics display used in modals & panels.
 * Excludes any action buttons (export/save) by design per spec.
 */
export const RouteSummary: React.FC<RouteSummaryProps> = ({
  distance,
  duration,
  elevationGain,
  bikeProfile = 'trekking',
  elevationLabel = 'Elevation',
  compact = false
}) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Paper elevation={1} sx={{ p: compact ? 1 : 1.5, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: compact ? 1 : 1.5 }}>
          <DirectionsBikeIcon color="primary" fontSize="small" />
          Route Summary
        </Typography>
        <Grid container spacing={compact ? 1 : 1.5}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <StraightenIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">Distance</Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {formatDistance(distance)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ScheduleIcon color="action" fontSize="small" />
              <Typography variant="caption" color="text.secondary">Duration</Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {formatDuration(duration)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LandscapeIcon color="action" fontSize="small" />
              <Typography variant="caption" color="text.secondary">{elevationLabel}</Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {formatElevation(elevationGain)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <DirectionsBikeIcon color="action" fontSize="small" />
              <Typography variant="caption" color="text.secondary">Bike Profile</Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {getBikeProfileDisplayName(bikeProfile)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default RouteSummary;
